import { differenceInSeconds } from 'date-fns';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cssWhen } from '../../../../shared/utils/themeUtils';
import { useOrderCurrencyExchangeState } from '../OrderCurrencyExchangeStateContext';
import classes from './OrderExchangeRateProgressBar.module.scss';

export type OrderExchangeRateProgressBarProps = {
  className?: string;
  refreshExchangeRate: () => Promise<void>;
};

export const OrderExchangeRateProgressBar = ({ className, refreshExchangeRate }: OrderExchangeRateProgressBarProps) => {
  const exchangeRateValidUntilInSeconds = useRef<number>(undefined);
  /** Ref for a setTimeout, that runs frequently to check if we pass the exchange rate validity time */
  const exchangeValidityCountdownTimer = useRef<number>(undefined);
  const [remainingTime, setRemainingTime] = useState<number>();
  const progressBarRef = useRef<HTMLDivElement | null>(null);
  const refrechExchangeRateRef = useRef(refreshExchangeRate);
  const { exchangeRate } = useOrderCurrencyExchangeState();

  useEffect(() => {
    refrechExchangeRateRef.current = refreshExchangeRate;
  }, [refreshExchangeRate]);

  /**
   * Updates the exchange rate based on the current exchange direction and amount
   *
   * This is not wrapped in useCallback, because it called in a setTimeout
   */
  const updateExchangeRateInner = async () => {
    // TODO NN-2576 - error handling
    // // if there is a temp order and the exchange rate is expired, we should reset the exchange
    // if (tempOrder) {
    //   resetCurrencyExchange();
    //   return;
    // }
    if (exchangeRate.isLoading) {
      // during the exchange rate loading, we should not trigger a refresh
      setRemainingTime(undefined);
      return;
    }

    clearInterval(exchangeValidityCountdownTimer.current);
    exchangeValidityCountdownTimer.current = undefined;

    try {
      setRemainingTime(undefined);
      await refrechExchangeRateRef.current();
    } catch (err) {
    } finally {
    }
  };

  const handleRemainingTime = async () => {
    if (!exchangeRate || exchangeValidityCountdownTimer.current || !exchangeRate.isLoaded || !exchangeRate.validTo) {
      return;
    }

    const newRemainingTime = differenceInSeconds(new Date(exchangeRate.validTo), new Date());
    if (newRemainingTime <= 0) {
      await updateExchangeRateInner();
    } else {
      setRemainingTime(newRemainingTime);

      exchangeValidityCountdownTimer.current = window.setTimeout(() => {
        exchangeValidityCountdownTimer.current = undefined;
        handleRemainingTime();
      }, 500);
    }
  };

  /**
   * This effect is responsible for the remaining time bar's animation.
   * We are using css transition-duration to animate the progress bar, and to achieve this we set the bar width to
   * 100% and then set the transition-duration to the remaining time.
   */
  useLayoutEffect(() => {
    if (exchangeRate.isLoaded && exchangeRate.validTo) {
      progressBarRef.current?.style.setProperty('transition', `none`);
      progressBarRef.current?.style.setProperty('transition-duration', `0s`);
      progressBarRef.current?.style.setProperty('width', '100%');
      exchangeRateValidUntilInSeconds.current = differenceInSeconds(new Date(exchangeRate.validTo), new Date());
      setTimeout(() => {
        if (!exchangeRate || !exchangeRate.validTo) {
          throw new Error(`Invalid exchange rate`);
        }
        progressBarRef.current?.style.setProperty(
          'transition',
          `all ${differenceInSeconds(new Date(exchangeRate.validTo), new Date())}s linear`,
        );
        progressBarRef.current?.style.setProperty('width', '0');
      }, 500);
    }
    return () => {
      if (exchangeValidityCountdownTimer.current) {
        clearTimeout(exchangeValidityCountdownTimer.current);
        exchangeValidityCountdownTimer.current = undefined;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchangeRate]);

  useLayoutEffect(() => {
    if (exchangeRate.isLoaded && exchangeRate.validTo) {
      if (exchangeValidityCountdownTimer.current) {
        clearInterval(exchangeValidityCountdownTimer.current);
        exchangeValidityCountdownTimer.current = undefined;
      }

      handleRemainingTime();
    } else if (exchangeRate.hasError) {
      // if there is an error, just clear the timer
      if (exchangeValidityCountdownTimer.current) {
        clearInterval(exchangeValidityCountdownTimer.current);
        exchangeValidityCountdownTimer.current = undefined;
      }
      if (progressBarRef.current) {
        progressBarRef.current.style.setProperty('transition', `all`);
        progressBarRef.current.style.setProperty('transition-duration', `0s`);
        progressBarRef.current.style.setProperty('width', '0');
      }
      setRemainingTime(undefined);
    } else if (!exchangeRate.isLoading && !exchangeRate.isLoaded && !exchangeRate.hasError) {
      // if the exchange rate is unitialized, we should not show the progress bar
      if (exchangeValidityCountdownTimer.current) {
        clearInterval(exchangeValidityCountdownTimer.current);
        exchangeValidityCountdownTimer.current = undefined;
      }

      if (progressBarRef.current) {
        progressBarRef.current.style.setProperty('transition', `none`);
        progressBarRef.current.style.setProperty('transition-duration', `0s`);
        progressBarRef.current.style.setProperty('width', '0');
      }
      setRemainingTime(undefined);
    }
    // Exhaustive deps ignore is important because this way we make sure that the function handling the progress bar state
    // (which is wrapped in a setTimeout) is having the correct function/value references
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchangeRate]);

  // TODO NN-2576 - error handling
  // useLayoutEffect(() => {
  //   if (validationError?.msg === Keys.CurrencyExchange.Errors.DEVIZAVALTAS_JOVAHAGYAS_ERVENYTELEN_ARFOLYAM) {
  //     clearValidationError();
  //
  //     updateExchangeRate().catch(err => {
  //       logger.error(`Validation marked exchange rate as invalid, but failed to refresh it`, err);
  //       resetCurrencyExchange();
  //     });
  //   }
  //   // Exhaustive deps ignore is important because updateExchangeRate is a function that is wrapped in a setTimeout and
  //   // we want to make sure that the function is having the correct function/value references
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [validationError, clearValidationError, logger, resetCurrencyExchange]);
  //

  if (!exchangeRate.isLoaded) {
    return <></>;
  }

  return (
    <>
      <div className={cssWhen(classes.exchangeProgress, className)}>
        <span className={cssWhen(classes.exchangeProgressTime)}>{remainingTime ? `${remainingTime}s` : ''}</span>

        <div
          ref={progressBarRef}
          className={classes.exchangeProgressBar}
        >
          <span>{remainingTime ? `${remainingTime}s` : ''}</span>
        </div>
      </div>
    </>
  );
};
